import React from 'react';

interface QuestionTextRendererProps {
    text: string;
    className?: string;
}

const QuestionTextRenderer: React.FC<QuestionTextRendererProps> = ({ text, className = '' }) => {
    const [statementStates, setStatementStates] = React.useState<Record<number, 'true' | 'false' | null>>({});

    React.useEffect(() => {
        setStatementStates({});
    }, [text]);

    // 0. PRE-FORMAT AND NORMALIZE TEXT (Enforce Line Breaks before parsing)
    const normalizedText = text
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        // Normalize list headers (List-I, List-II, List 1, etc.) to start on a new line
        .replace(/(?:\n|\s)+(List[- ]?(?:[IVX]+|[0-9]+))\b/gi, '\n$1')
        // Normalize numbered list items (e.g., " 1. ", " 2. ")
        .replace(/(?:\n|\s)+([0-9]+[\.\)])\s+/g, '\n$1 ')
        // Normalize lettered list items (e.g., " A. ", " P. ")
        .replace(/(?:\n|\s)+([A-Za-z][\.\)])\s+/g, '\n$1 ');

    // 1. ASSERTION & REASON PARSING LOGIC
    const isAssertionReason = /Assertion/i.test(normalizedText) && /Reason/i.test(normalizedText);

    if (isAssertionReason) {
        try {
            const assertionMatch = normalizedText.match(/Assertion\s*(?:\(A\))?\s*:?\s*/i);
            const reasonMatch = normalizedText.match(/Reason\s*(?:\(R\))?\s*:?\s*/i);

            if (assertionMatch && reasonMatch) {
                const assertionIdx = assertionMatch.index!;
                const reasonIdx = reasonMatch.index!;

                if (assertionIdx < reasonIdx) {
                    const introText = normalizedText.substring(0, assertionIdx).trim();
                    const assertionPrefixLen = assertionMatch[0].length;
                    const assertionText = normalizedText.substring(assertionIdx + assertionPrefixLen, reasonIdx).trim();
                    const reasonPrefixLen = reasonMatch[0].length;
                    const reasonText = normalizedText.substring(reasonIdx + reasonPrefixLen).trim();

                    if (assertionText && reasonText) {
                        return (
                            <div className={`space-y-4 w-full ${className}`}>
                                {introText && <p className="leading-relaxed whitespace-pre-line text-slate-900 dark:text-slate-100 font-medium">{introText}</p>}
                                
                                <div className="space-y-3">
                                    {/* Assertion Card */}
                                    <div className="p-4 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-sm">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-400 mb-2.5 uppercase tracking-wide">
                                            Assertion (A)
                                        </span>
                                        <p className="text-slate-800 dark:text-slate-200 leading-relaxed font-medium text-lg">{assertionText}</p>
                                    </div>

                                    {/* Reason Card */}
                                    <div className="p-4 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-sm">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-400 mb-2.5 uppercase tracking-wide">
                                            Reason (R)
                                        </span>
                                        <p className="text-slate-800 dark:text-slate-200 leading-relaxed font-medium text-lg">{reasonText}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                }
            }
        } catch (e) {
            console.warn('Assertion-Reason parsing failed, falling back', e);
        }
    }

    // 2. MATCH MATRIX PARSING LOGIC (List-I & List-II Columns)
    const lines = normalizedText.split('\n');
    const isList1Header = (line: string) => /^\s*(List[- ]?(I|1))\b/i.test(line);
    const isList2Header = (line: string) => /^\s*(List[- ]?(II|2))\b/i.test(line);

    let list1HeaderIdx = -1;
    let list2HeaderIdx = -1;

    for (let i = 0; i < lines.length; i++) {
        if (isList1Header(lines[i])) {
            list1HeaderIdx = i;
        }
        if (isList2Header(lines[i])) {
            list2HeaderIdx = i;
        }
    }

    const isMatchMatrix = list1HeaderIdx !== -1 && list2HeaderIdx !== -1 && list1HeaderIdx < list2HeaderIdx;

    if (isMatchMatrix) {
        try {
            const introText = lines.slice(0, list1HeaderIdx).join('\n').trim();
            const listIHeader = lines[list1HeaderIdx].trim();
            const listIIHeader = lines[list2HeaderIdx].trim();

            const listIItems: string[] = [];
            for (let i = list1HeaderIdx + 1; i < list2HeaderIdx; i++) {
                const line = lines[i].trim();
                if (line) {
                    listIItems.push(line);
                }
            }

            const listIIItems: string[] = [];
            let footerText = '';
            let foundFooter = false;

            for (let i = list2HeaderIdx + 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                // Check if this line looks like a list item bullet (e.g. "P. ", "Q. ", "A. ", "1. ", "a) ")
                const isBullet = /^[A-Za-z0-9]+[\.\)]\s+/.test(line);

                if (foundFooter) {
                    footerText += (footerText ? '\n' : '') + line;
                } else if (!isBullet && listIIItems.length > 0) {
                    foundFooter = true;
                    footerText += line;
                } else {
                    listIIItems.push(line);
                }
            }

            if (listIItems.length > 0 && listIIItems.length > 0) {
                return (
                    <div className={`space-y-4 w-full ${className}`}>
                        {introText && <p className="leading-relaxed whitespace-pre-line text-slate-900 dark:text-slate-100 font-medium mb-3">{introText}</p>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 dark:bg-slate-900/30 p-5 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-sm">
                            {/* List I Column */}
                            <div>
                                <h4 className="font-bold text-slate-800 dark:text-white mb-3 border-b border-slate-200 dark:border-slate-700 pb-1.5 text-sm uppercase tracking-wide">{listIHeader}</h4>
                                <div className="space-y-2.5">
                                    {listIItems.map((item, idx) => (
                                        <div key={`list-i-${idx}`} className="text-slate-700 dark:text-slate-350 text-[15px] leading-relaxed font-medium">{item}</div>
                                    ))}
                                </div>
                            </div>

                            {/* List II Column */}
                            <div>
                                <h4 className="font-bold text-slate-800 dark:text-white mb-3 border-b border-slate-200 dark:border-slate-700 pb-1.5 text-sm uppercase tracking-wide">{listIIHeader}</h4>
                                <div className="space-y-2.5">
                                    {listIIItems.map((item, idx) => (
                                        <div key={`list-ii-${idx}`} className="text-slate-700 dark:text-slate-350 text-[15px] leading-relaxed font-medium">{item}</div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {footerText && <p className="mt-4 text-slate-600 dark:text-slate-400 italic text-sm leading-relaxed">{footerText}</p>}
                    </div>
                );
            }
        } catch (e) {
            console.warn('Match Matrix parsing failed, falling back', e);
        }
    }

    // 3. FALLBACK DEFAULT REGEX FORMATTING (With styled prompt highlight and statement list guide)
    const promptRegex = /(Which\s+(?:of|among)\s+the\s+(?:following\s+)?(?:statements|pairs|options|above|following|laws)?|How\s+many\s+of\s+the\s+(?:following\s+)?(?:statements|pairs|options|above|following)?|Select\s+the\s+(?:correct|incorrect|following)|Identify\s+the\s+(?:correct|incorrect|following)|Choose\s+the\s+(?:correct|incorrect|following)|Which\s+of\s+the\s+above|How\s+many\s+of\s+the\s+above)\b.*?$/i;
    const promptMatch = normalizedText.match(promptRegex);

    let mainText = normalizedText;
    let promptText = '';

    if (promptMatch) {
        const index = promptMatch.index!;
        mainText = normalizedText.substring(0, index).trim();
        promptText = normalizedText.substring(index).trim();
    }

    // Extract statements from mainText
    const mainLines = mainText.split('\n');
    const introLines: string[] = [];
    const statementLines: string[] = [];
    const outroLines: string[] = [];
    let foundStatement = false;
    let finishedStatements = false;

    for (let i = 0; i < mainLines.length; i++) {
        const line = mainLines[i];
        const isStatementLine = /^\s*\d+[\.\)]\s+/.test(line);

        if (isStatementLine) {
            foundStatement = true;
            statementLines.push(line);
        } else if (foundStatement && !isStatementLine) {
            if (line.trim() === '') continue;
            finishedStatements = true;
            outroLines.push(line);
        } else {
            introLines.push(line);
        }
    }

    const renderPromptText = (prompt: string) => {
        const matchIncorrect = prompt.match(/incorrect/i);
        if (matchIncorrect) {
            const word = matchIncorrect[0];
            const parts = prompt.split(new RegExp(word, 'g'));
            return (
                <span className="font-semibold text-slate-800 dark:text-slate-200 text-lg">
                    {parts[0]}
                    <span className="text-rose-600 dark:text-rose-400 font-bold bg-rose-50 dark:bg-rose-950/20 px-1.5 py-0.5 rounded border border-rose-100 dark:border-rose-900/30">
                        {word}
                    </span>
                    {parts[1]}
                </span>
            );
        }
        
        const matchCorrect = prompt.match(/correct/i);
        if (matchCorrect) {
            const word = matchCorrect[0];
            const parts = prompt.split(new RegExp(word, 'g'));
            return (
                <span className="font-semibold text-slate-800 dark:text-slate-200 text-lg">
                    {parts[0]}
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/20 px-1.5 py-0.5 rounded border border-emerald-100 dark:border-emerald-900/30">
                        {word}
                    </span>
                    {parts[1]}
                </span>
            );
        }
        return <span className="font-semibold text-slate-800 dark:text-slate-200 text-lg">{prompt}</span>;
    };

    return (
        <div className={`leading-relaxed whitespace-pre-line text-slate-900 dark:text-slate-100 w-full ${className}`}>
            {/* Intro text */}
            {introLines.length > 0 && (
                <div className="mb-4">{introLines.join('\n')}</div>
            )}

            {/* Statements List Guide with T/F Interactive Toggles */}
            {statementLines.length > 0 && (
                <div className="my-5 pl-4 border-l-2 border-orange-500/30 dark:border-orange-500/20 bg-slate-50/40 dark:bg-slate-900/10 py-3.5 rounded-r-xl space-y-3.5 shadow-inner">
                    {statementLines.map((line, idx) => {
                        const state = statementStates[idx];
                        const isTrue = state === 'true';
                        const isFalse = state === 'false';

                        const match = line.match(/^\s*(\d+)[\.\)]\s+(.*)$/);
                        const num = match ? match[1] : (idx + 1).toString();
                        const statementContent = match ? match[2] : line;

                        return (
                            <div key={idx} className="flex items-start gap-3 group/item">
                                {/* Action buttons */}
                                <div className="flex items-center gap-1.5 mt-0.5 flex-shrink-0">
                                    <button
                                        onClick={() => setStatementStates(prev => ({
                                            ...prev,
                                            [idx]: prev[idx] === 'true' ? null : 'true'
                                        }))}
                                        title="Mark as True"
                                        className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold transition-all border
                                            ${isTrue
                                                ? 'bg-emerald-500 border-emerald-500 text-white shadow-sm'
                                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 hover:text-emerald-500'
                                            }`}
                                    >
                                        ✓
                                    </button>
                                    <button
                                        onClick={() => setStatementStates(prev => ({
                                            ...prev,
                                            [idx]: prev[idx] === 'false' ? null : 'false'
                                        }))}
                                        title="Mark as False"
                                        className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold transition-all border
                                            ${isFalse
                                                ? 'bg-rose-500 border-rose-500 text-white shadow-sm'
                                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 hover:text-rose-500'
                                            }`}
                                    >
                                        ✗
                                    </button>
                                </div>

                                {/* Text content */}
                                <div className={`flex items-start transition-all duration-200
                                    ${isTrue ? 'text-emerald-600 dark:text-emerald-400 font-medium' : ''}
                                    ${isFalse ? 'text-slate-400 dark:text-slate-500 line-through opacity-60' : 'text-slate-700 dark:text-slate-300'}
                                `}>
                                    <span className="font-bold mr-2 text-slate-500 dark:text-slate-400">{num}.</span>
                                    <span className="leading-relaxed">{statementContent}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Outro text (if any) */}
            {outroLines.length > 0 && (
                <div className="my-4">{outroLines.join('\n')}</div>
            )}

            {/* Prompt highlight */}
            {promptText && (
                <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700/60 leading-relaxed">
                    {renderPromptText(promptText)}
                </div>
            )}
        </div>
    );
};

export default QuestionTextRenderer;
